// The card's aggregator: the period's scanned games that reached an ending,
// by how the ending went (`GameFacts.endingCase`): won, lost on time,
// slipped, missed, lost or drawn. Every such game is in exactly one case, so
// the six add up. Registered with the game store by this plugin's setup; the
// store runs it over the last 30 days' games on every change and keeps the
// result.
import { aggregatorId, collect, ENDING_CASES, type Accumulator, type Aggregator, type EndingCase } from '@chess/api-model';

/** What the card shows: the games in each case; plain data, so the card and its stories need no store. */
export type EndingCasesResult = Record<EndingCase, number>;

export class EndingCasesAggregator implements Aggregator<EndingCasesResult> {
  readonly id = aggregatorId('ending-cases');

  start(): Accumulator<EndingCasesResult> {
    return collect((games) => {
      const counts: EndingCasesResult = { won: 0, time: 0, slipped: 0, missed: 0, lost: 0, drawn: 0 };
      for (const g of games) if (g.scanned && g.endingCase) counts[g.endingCase] += 1;
      return counts;
    });
  }
}

/** The games that reached an ending: every case together. */
export const endingsOf = (r: EndingCasesResult) => ENDING_CASES.reduce((sum, c) => sum + r[c], 0);

/** The endings the reader did not win: every case but won ("Your losses"). */
/** The endings lost or let slip: every case but won and a plain draw (the card's slices). */
export const lossesOf = (r: EndingCasesResult) => endingsOf(r) - r.won - r.drawn;
